import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface GooglePlacesData {
  rating: number;
  userRatingCount: number;
  businessStatus?: string;
  lastUpdated?: Date;
  fromCache?: boolean;
}

interface UseGooglePlacesDataReturn {
  data: GooglePlacesData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  lastUpdated: Date | null;
  fromCache: boolean;
}

// Cache TTL: 24 hours
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const logApiUsage = async (
  placeId: string,
  source: string,
  statusCode: number,
  wasFromCache: boolean,
  businessId?: string
) => {
  try {
    // Estimate API cost - Google Places API Place Details costs $5 per 1000 requests
    const apiCostUsd = wasFromCache ? 0 : 0.005;

    await supabase.from('google_places_api_usage_log').insert({
      endpoint: `places/${placeId}`,
      place_id: placeId,
      business_id: businessId || null,
      source: source,
      response_status: statusCode,
      cache_hit: wasFromCache,
      api_cost_usd: apiCostUsd,
      user_id: null,
      request_metadata: {
        timestamp: new Date().toISOString(),
        field_mask: 'rating,userRatingCount,businessStatus'
      }
    });
  } catch (error) {
    // Failed to log API usage
  }
};

const fetchCachedData = async (placeId: string): Promise<GooglePlacesData | null> => {
  try {
    const { data: business, error: dbError } = await supabase
      .from('businesses')
      .select(`
        id,
        cached_google_rating,
        cached_google_review_count,
        cached_google_business_status,
        google_rating_cache_updated_at
      `)
      .eq('google_place_id', placeId)
      .single();

    if (dbError) {
      return null;
    }

    if (!business?.google_rating_cache_updated_at) {
      return null; // No cache available
    }

    const cacheAge = new Date().getTime() - new Date(business.google_rating_cache_updated_at).getTime();
    const isCacheValid = cacheAge < CACHE_TTL_MS;

    if (!isCacheValid) {
      return null; // Cache expired
    }

    // Log cache hit
    await logApiUsage(placeId, 'cache_hit', 200, true, business.id);

    return {
      rating: business.cached_google_rating || 0,
      userRatingCount: business.cached_google_review_count || 0,
      businessStatus: business.cached_google_business_status,
      lastUpdated: new Date(business.google_rating_cache_updated_at),
      fromCache: true
    };
  } catch (err) {
    return null;
  }
};

const fetchFreshData = async (placeId: string): Promise<GooglePlacesData> => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    throw new Error('Google Maps API key not configured');
  }

  const url = `https://places.googleapis.com/v1/places/${placeId}`;

  let businessId: string | undefined;

  // Try to get business ID for logging
  try {
    const { data: business } = await supabase
      .from('businesses')
      .select('id')
      .eq('google_place_id', placeId)
      .single();
    businessId = business?.id;
  } catch (err) {
    // Could not find business ID for logging
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'rating,userRatingCount,businessStatus'
    }
  });

  // Log API usage regardless of success/failure
  await logApiUsage(placeId, 'cache_miss', response.status, false, businessId);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Google Place not found');
    } else if (response.status === 403) {
      throw new Error('API access denied - check your API key permissions');
    } else {
      throw new Error(`Google Places API error: ${response.status}`);
    }
  }

  const responseData = await response.json();

  // Validate the response data
  if (!responseData.rating && !responseData.userRatingCount) {
    throw new Error('No rating data available for this place');
  }

  const freshData: GooglePlacesData = {
    rating: responseData.rating || 0,
    userRatingCount: responseData.userRatingCount || 0,
    businessStatus: responseData.businessStatus,
    lastUpdated: new Date(),
    fromCache: false
  };

  // Update cache in background (don't wait for it)
  supabase.auth.getUser().then(({ data: { user } }) => {
    if (user) {
      supabase
        .from('businesses')
        .update({
          cached_google_rating: freshData.rating,
          cached_google_review_count: freshData.userRatingCount,
          cached_google_business_status: freshData.businessStatus,
          google_rating_cache_updated_at: freshData.lastUpdated!.toISOString()
        })
        .eq('google_place_id', placeId)
        .eq('user_id', user.id)
        .then(({ error }) => {
          if (error) {
            console.error('Failed to update Google Places cache:', error);
          }
        });
    }
  });

  return freshData;
};

const fetchGooglePlacesData = async (placeId: string): Promise<GooglePlacesData> => {
  // Try cache first
  const cachedData = await fetchCachedData(placeId);
  if (cachedData) {
    return cachedData;
  }

  // Fetch fresh data from Google API
  return fetchFreshData(placeId);
};

export const useGooglePlacesData = (placeId?: string): UseGooglePlacesDataReturn => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['googlePlacesData', placeId],
    queryFn: () => fetchGooglePlacesData(placeId!),
    enabled: !!placeId,
    staleTime: CACHE_TTL_MS, // Consider data fresh for 24 hours
    gcTime: CACHE_TTL_MS, // Keep in cache for 24 hours
    retry: 1,
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
  });

  return {
    data: data || null,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch: () => { refetch(); },
    lastUpdated: data?.lastUpdated || null,
    fromCache: data?.fromCache || false
  };
};

export default useGooglePlacesData;
